import { makeAutoObservable } from 'mobx'
import { me } from 'store/me'
import { opt } from 'store/opt'
import { EmailFormStore } from 'store/profile/EmailFormStore'
import { ProfileCommonFormStore } from 'store/profile/ProfileCommonFormStore'
import { ProfileGenderFormStore } from 'store/profile/ProfileGenderFormStore'
import { SimpleForm } from 'store/profile/SimpleForm'
import { stat } from 'store/stat'
import { TermFormStore } from 'store/term/term'
import { AvatarType } from 'type/Avatar'
import { BaseIdentityRequest, CreateIdentityRequest, CreateIdentityResponse, Gender, Identity, UpdateIdentityRequest } from 'type/Identity'
import { IdentityConfig } from 'type/IdentityConfig'
import { LengthUnit } from 'type/LengthUnit'
import { Photo } from 'type/Options'
import { SilentError } from 'util/error'
import { getNextKey } from 'util/id'
import { BasicForm, getBasicFirstStep } from './BasicForm'

type ProfileStep = 'profile' | 'email' | 'data' | 'help' | 'sizes' | 'full'

function getAvailableAvatarOptions(): AvatarType[] {
  const options: AvatarType[] = []
  options.push('camera')
  if (opt.qr_upload) options.push('qr')
  if (opt.options.selfies?.on) options.push('preset')

  return options
}

function getType(it: Identity) {
  const fullBody = it.user_url || it.body1_url
  const saved = it.identity_mode === 3 ? 'simple' : fullBody ? 'full' : 'selfie'
  return opt.photo.includes(saved) ? saved : opt.photo_def
}

class ProfileStore {
  key = getNextKey()
  config: IdentityConfig
  identity_id: string = ''
  gender: Gender = 'female'
  picture?: string
  term?: TermFormStore
  isNew: boolean = false
  private _type: Photo = 'full'

  private _step: ProfileStep = 'profile'
  private _camera = false

  readonly common: ProfileCommonFormStore
  readonly email: EmailFormStore
  readonly basic: BasicForm
  readonly simple: SimpleForm
  private female: ProfileGenderFormStore
  private male: ProfileGenderFormStore

  saving?: Promise<void>

  private request: Partial<BaseIdentityRequest>

  constructor(config: IdentityConfig, identity: Identity | undefined) {
    makeAutoObservable(this)
    this.config = config

    const gender = identity?.gender ?? opt.options.gender ?? 'female'
    const female = gender === 'female' ? identity : undefined
    const male = gender === 'male' ? identity : undefined

    this.identity_id = identity?.identity_id || ''
    this.gender = gender
    this._type = identity ? getType(identity) : opt.photo_def

    this.common = new ProfileCommonFormStore(identity)
    this.email = new EmailFormStore()
    this.basic = new BasicForm()
    // Существующий профиль пришёл из API — его флоу проходить уже не нужно.
    this.basic.done = !!identity
    this.simple = new SimpleForm()
    this.female = new ProfileGenderFormStore('female', config.female, female, this.common, this.type)
    this.male = new ProfileGenderFormStore('male', config.male, male, this.common, this.type)

    this.updatePicture()
    this.reset()

    this.request = identity ? this.buildTempRequest() : {}
  }

  get changed(): boolean {
    const request = this.buildTempRequest()
    const same = JSON.stringify(this.request) === JSON.stringify(request)
    return !same
  }

  get type(): Photo {
    return this._type
  }

  set type(type: Photo) {
    this._type = type
    if (!this.identity_id) {
      this.female = new ProfileGenderFormStore('female', this.config.female, undefined, this.common, type)
      this.male = new ProfileGenderFormStore('male', this.config.male, undefined, this.common, type)
    }
  }

  get pic(): string | undefined {
    return this.common.selfies[0].url || this.common.selfies[1].url || this.common.userPhoto.url
  }

  get hasSelfie(): boolean {
    return this.common.selfies.some(it => !it.empty)
  }

  get step(): ProfileStep {
    return this._step
  }

  set step(step: ProfileStep) {
    this._step = step
    this.term = step === 'data' ? new TermFormStore() : undefined
  }

  get camera(): boolean {
    return this._camera
  }

  set camera(value: boolean) {
    this._camera = value
  }

  get canSave(): boolean {
    return !this.saving && this.form.canSave
  }

  get unit(): LengthUnit {
    return this.common.unit
  }

  set unit(unit: LengthUnit) {
    this.common.unit = unit
  }

  get form(): ProfileGenderFormStore {
    return this.gender === 'male' ? this.male : this.female
  }

  setGender(value: Gender): void {
    this.gender = value
  }

  reset() {
    this.step = 'profile'
    this.camera = opt.camera
    this.common.reset()
    this.email.reset()
    this.female.reset()
    this.male.reset()
  }

  async buildCreateRequest(user_id: string): Promise<CreateIdentityRequest> {
    const request = await this.buildBaseRequest()
    return { user_id, ...request }
  }

  async buildUpdateRequest(user_id: string): Promise<UpdateIdentityRequest> {
    const { identity_id } = this
    const request = await this.buildBaseRequest()
    return { user_id, identity_id, ...request }
  }

  async beforeSave() {
    await this.common.beforeSave()
    this.form.beforeSave()
    this.updatePicture()
  }

  afterCreate(response: CreateIdentityResponse) {
    this.identity_id = response.identity_id
    this.isNew = false
    this.afterUpdate()
  }

  afterUpdate() {
    this.isNew = false
    this.sendAvatarEvent()
    this.updatePicture()
    this.request = this.buildTempRequest()
  }

  onPicture(picture: string) {
    this.common.selfies[0].setUrl(picture)
    this.common.selfies[1].clear()
    this.updatePicture()
  }

  applyIdentity(identity: Identity) {
    this.identity_id = identity.identity_id
    this.common.applySelfies(identity.face1_url, identity.face2_url)
    // Фото в полный рост тоже приезжает извне — например, после загрузки по QR с телефона,
    // но отброшенное пользователем обратно не принимаем.
    this.common.applyUserPhoto(identity.user_url || identity.body1_url)
    this.updatePicture()
  }

  // Юзер бросил создание профиля на середине: флоу не пройден до конца, но фото уже
  // загружено или шаг сдвинут. На identity_id ориентироваться нельзя — при загрузке
  // фото по QR профиль создаётся на телефоне, хотя флоу в панели ещё не пройден.
  get basicUnfinished(): boolean {
    if (!opt.basic) return false
    if (this.basic.done) return false
    return this.basic.step !== getBasicFirstStep() || this.common.canSave
  }

  applyBasic() {
    const { gender, shape, body, height } = this.basic

    if (gender) this.gender = gender
    if (shape) this.form.shape = shape
    if (body) this.form.body = body
    if (height) this.form.height = height
  }

  async ensureSaved() {
    while (this.saving) {
      await this.saving
    }
  }

  private updatePicture() {
    this.picture = this.pic
    me.picture = this.pic
  }

  private sendAvatarEvent() {
    const avatar_type = this.type === 'full'
      ? this.common.userPhoto.avatarType
      : this.common.selfies[0].avatarType
    const available_options = getAvailableAvatarOptions()
    if (avatar_type) stat.setAvatar(avatar_type, available_options)
  }

  private async buildBaseRequest(): Promise<BaseIdentityRequest> {
    try {
      const request = await this.form.buildBaseRequest(this.type)
      this.common.afterRequest()
      return request
    } catch (e) {
      console.info('build request error', e)
      throw new SilentError()
    }
  }

  private buildTempRequest() {
    return this.form.buildTempRequest(this.type)
  }
}

export default ProfileStore
